import { LockIcon } from "@chakra-ui/icons";
import { ContactFootnote } from "components/common/contact-footnote";
import settings from "config/settings.json";
import { ErrorLayout } from "layouts/error";
import { LoadingProgress, SignInContent, SignInLayout, SubTitleContent } from "layouts/sign-in";
import { useMsal } from "@azure/msal-react";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";

const { healthCheck: { serviceUnavailableContactEmail: CONTACT_EMAIL } } = settings;

interface ILoadingPageProps {
  isError?: boolean;
  name?: string;
}

const SignInLoadingErrorFootnote: React.FC = () => {
  return (
    <ContactFootnote
      testId="lpContactLink"
      email={CONTACT_EMAIL}
      i18nKey="signIn.contactFootnote"
      defaultText="Please <contactLink>contact your administrator</contactLink>."
    />
  );
};

const SignInLoadingError: React.FC = () => {
  const { t } = useTranslation();
  const { instance } = useMsal();
  const signInError = t("signIn.error", "You don't have any applications assigned to your account.");
  const errorTitle = t("signIn.errorTitle", "Oops!");
  const signOut = t("signIn.signOut", "Sign out");

  const onSignOutClick = useCallback(() => {
    instance.logoutRedirect();
  }, [instance]);

  return (
    <ErrorLayout
      title={errorTitle}
      heading={errorTitle}
      icon={<LockIcon />}
      message={signInError}
      messageTestId="lpErrorMsg"
      footnote={<SignInLoadingErrorFootnote/>}
      actions={[
        {
          text: signOut,
          testId: "lpSignOutButton",
          onClick: onSignOutClick,
          primary: true
        }
      ]}/>
  );
};

const SignInLoadingProgressContent: React.FC = () => {
  const { t } = useTranslation();
  const weAreLoadingYourAccount = t("signIn.weAreLoadingYourAccount", "We are loading your account.");
  const pleaseWait = t("signIn.pleaseWait", "Please wait...");

  return (
    <SubTitleContent>
      {weAreLoadingYourAccount}<br/>
      {pleaseWait}
    </SubTitleContent>
  );
};

const SignInLoadingProgress: React.FC<{ name: string }> = ({ name }) => {
  const { t } = useTranslation();
  const welcomeBack = t("signIn.welcomeBack", "Welcome back");

  return (
    <SignInLayout>
      <SignInContent
        title={`${welcomeBack} ${name}`}
        subTitle={<SignInLoadingProgressContent/>}
        titleTestId="lpWelcomeTitle"
        subTitleTestId="lpWelcomeMsg"
      />
      <LoadingProgress/>
    </SignInLayout>
  );
};

const SignInLoading: React.FC<ILoadingPageProps> = ({ isError, name }) => {
  if (isError) {
    return (
      <SignInLoadingError></SignInLoadingError>
    );
  }

  return (
    <SignInLoadingProgress name={name ?? ""}></SignInLoadingProgress>
  );
};

export default SignInLoading;
